<template>
  <BaseFooter
    :buttonOptions="buttonOptions"
    @select="onOptionSelect"
  >
    <template #primaryAction>
      <BaseButton
        v-if="outstandingAmount > 0"
        submitForm
        :disabled="!order.items.length"
        :loading="isSubmitting"
        color="warning"
        v-test="'order-save-unpaid'"
      >
        {{ t('register.save_as_unpaid') }}
      </BaseButton>
      <BaseButton
        v-else
        :disabled="disableCheckout"
        :loading="isSubmitting"
        submitForm
        v-test="'order-complete-sale'"
      >
        {{ $t(`global.actions.complete_${orderKey()}`) }}
      </BaseButton>
    </template>
  </BaseFooter>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { compliantKey, orderKey } from '@/helpers/compliant-register';
import { modal } from '@/helpers/ui';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useRegisterDrawerStore } from '@/stores/register-drawer';
import BaseFooter from '../../Footer.vue';

const { order } = useRegisterOrderStore();
const { totalPrice, outstandingAmount, correction } = storeToRefs(
  useRegisterOrderStore(),
);
const { isOpen: isDrawerOpen, isLoadingDrawerStatus } = storeToRefs(
  useRegisterDrawerStore(),
);
const router = useRouter();
const { t } = useI18n();
const { cashupEnabled, savingOrder, isEditing } = storeToRefs(
  useRegisterStore(),
);

const isSubmitting = computed(
  () => savingOrder.value || isLoadingDrawerStatus.value,
);

const canModify = computed(
  () =>
    !cashupEnabled.value ||
    (cashupEnabled.value &&
      isDrawerOpen.value &&
      order.permissions?.editOrderItems),
);

const disableCheckout = computed(() => {
  const change = outstandingAmount.value < 0 ? outstandingAmount.value * -1 : 0;

  return totalPrice.value < 0 && change > 0;
});

const handleModifyClick = () => {
  correction.value = order.state === 'PAID' || order.state === 'OUTSTANDING';
  isEditing.value = true;
  router.push({ name: 'register', query: { action: 'modify' } });
};

const buttonOptions = computed<any[]>(() => {
  return [
    ...(canModify.value
      ? [
          {
            icon: 'edit',
            label: t('global.actions.edit_sale'),
            value: 'modify',
          },
        ]
      : []),
    {
      icon: 'message-circle',
      label: order.note
        ? t('global.actions.edit_note')
        : t('global.actions.add_note'),
      value: 'note',
    },
    {
      icon: 'delete',
      label: t('global.actions.cancel_sale'),
      value: 'reset',
      color: 'error',
      routerLink: { name: 'register-builder' },
    },
  ];
});

const onOptionSelect = (option: string) => {
  if (option === 'modify') {
    handleModifyClick();
  }
};
</script>
